import { useEffect, useState } from "react";
import Uppy from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import {ALLOWED_FILE_TYPES} from "../common/constants";
const uppy = new Uppy({
  id: "chat-widget-file-sharing",
  restrictions: {
    ALLOWED_FILE_TYPES,
  },
});

const initializeTransloadit = (config) => {
  const { REACT_APP_TRANSLOADIT_AUTH_KEY, REACT_APP_TRANSLOADIT_TEMPLATE_ID } = config || {};
  uppy.use(Transloadit, {
    service: "https://api2.transloadit.com",
    waitForEncoding: true,
    params: {
      auth: { key: REACT_APP_TRANSLOADIT_AUTH_KEY },
      template_id: REACT_APP_TRANSLOADIT_TEMPLATE_ID,
    },
  });
  return uppy;
};

export const convertFileToDataURI = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = () => {
      reject();
    };
  });
};

export const addFilesToUppy = (files, setProgressBarCompletion, id, removeFromConversation, setUploadErrorMessage) => {
  files.forEach((file) => {
    try {
      uppy.addFile({
        source: "Local",
        name: file.name,
        type: file.type,
        data: file,
        isRemote: false,
        meta:{
          id
        },
      });
    } catch (error) {
      if(error.toString().includes("Error: Cannot add the duplicate file")){
        removeFromConversation({id});
        setUploadErrorMessage('Error: Cannot add the duplicate file.');
        setProgressBarCompletion(0);
      }
      else {
        setUploadErrorMessage('Error: Upload failed.');
        removeFromConversation({id});
        setProgressBarCompletion(0);
      }
      // do nothing
    }
  });
};

export const removeFileFromUppyState = (id) => {
  uppy.removeFile(id);
};

export const getFilesFromUppyState = () => {
  const files = uppy.getFiles().map((file) => ({
    name: file.name,
    id: file.id,
    mime: file.type,
    size: file.size,
    type: file.extension,
    url: "",
    fileId: file.meta && file.meta.id,
  }));
  return files;
};

// custom hook to detect file upload progress
export const useFileUploadProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const getProgressPercentage = (percentage) => {
      setProgress(0.8 * percentage);

    };
    uppy.on("progress", getProgressPercentage);
    return () => {
      uppy.off("progress", getProgressPercentage);
    };
  }, []);
  return progress;
};

export const useFileErrorHandler = (setError, setProgressBarCompletion, setPreviewRetry) => {
  useEffect(() => {
    const handleError = (error) => {
      if(error) {
        console.log(error);
        uppy.cancelAll();
        setError("Upload failed, Please try again.");
        setProgressBarCompletion(0);
        if (typeof setPreviewRetry === 'function') {
          setPreviewRetry(true);
        }
      }
    }
    uppy.on("error", handleError);
    return () => {
      uppy.off("error", handleError);
    };
  }, [setError, setProgressBarCompletion, setPreviewRetry]);
};

export const uploadFilesToTransloadit = async ({ sendToPusher }, config, setProgressBarCompletion, setUploadErrorMessage, setPreviewRetry) => {
  let transloaditInstance = null;
  try {
    transloaditInstance = initializeTransloadit(config);
    const response = await transloaditInstance.upload();
    if (response.failed.length > 0) {
      console.error('Errors:');
      setProgressBarCompletion(0);
      setUploadErrorMessage('Error: Upload failed.');
    }
    else{
      const { transloadit } = response;
      const { assembly_url } = transloadit[0];
      const files = getFilesFromUppyState();
      sendToPusher({ attachments: { files, assemblyUrl: assembly_url }});
    }
  } catch (error) {
    if(error) {
      if (transloaditInstance && typeof transloaditInstance.cancelAll === 'function') {
        transloaditInstance.cancelAll();
      }
      console.log(error);
      setUploadErrorMessage("Upload failed, Please try again.");
      setProgressBarCompletion(0);
      if (typeof setPreviewRetry === 'function') {
        setPreviewRetry(true);
      }
    }
  } finally {
    if (transloaditInstance && typeof transloaditInstance.close === 'function') {
      transloaditInstance.close();
    }
  }
};

export const useUppyUiConnector = () => {
  const [files, setFiles] = useState([]);

  useEffect(() => {
    const fileAddedListener = () => {
      const storedFiles = getFilesFromUppyState();
      setFiles(storedFiles);
    };
    uppy.on("files-added", fileAddedListener);
    uppy.on("file-removed", fileAddedListener);
    return () => {
      uppy.off("files-added", fileAddedListener);
      uppy.off("file-removed", fileAddedListener);
    };
  }, []);

  return files;
};
